import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import complianceReportsCustomizationService from '../../../services/complianceReportsCustomizationService';

const ReportSchedulerModal = ({ onClose, onSave }) => {
  const [templates, setTemplates] = useState([]);
  const [formData, setFormData] = useState({
    scheduleName: '',
    reportTemplateId: '',
    deliveryFrequency: 'weekly',
    deliveryDayOfWeek: 1,
    deliveryDayOfMonth: 1,
    deliveryTime: '09:00',
    reportFormat: 'png',
    recipientEmails: '',
    recipientDepartments: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const frequencyOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' }
  ];

  const formatOptions = [
    { value: 'pdf', label: 'PDF' },
    { value: 'excel', label: 'Excel' },
    { value: 'csv', label: 'CSV' },
    { value: 'png', label: 'PNG (Image with Kina formatting)' }
  ];

  const dayOfWeekOptions = [
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
    { value: 7, label: 'Sunday' }
  ];

  const departmentOptions = ['HR', 'Finance', 'Operations', 'IT', 'Legal & Compliance', 'Executive'];

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await complianceReportsCustomizationService?.getAllTemplates();
      setTemplates(data);
      if (data?.length > 0) {
        setFormData(prev => ({ ...prev, reportTemplateId: data?.[0]?.id }));
      }
    } catch (err) {
      setError(err?.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleDepartmentToggle = (dept) => {
    setFormData(prev => ({
      ...prev,
      recipientDepartments: prev?.recipientDepartments?.includes(dept)
        ? prev?.recipientDepartments?.filter(d => d !== dept)
        : [...prev?.recipientDepartments, dept]
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      if (!formData?.scheduleName?.trim()) {
        setError('Please enter a schedule name');
        return;
      }

      if (!formData?.reportTemplateId) {
        setError('Please select a report template');
        return;
      }

      const emails = formData?.recipientEmails
        ?.split(',')
        ?.map(e => e?.trim())
        ?.filter(e => e);

      if (emails?.length === 0 && formData?.recipientDepartments?.length === 0) {
        setError('Please add at least one recipient email or department');
        return;
      }

      const nextDeliveryDate = calculateNextDeliveryDate(
        formData?.deliveryFrequency,
        formData?.deliveryDayOfWeek,
        formData?.deliveryDayOfMonth
      );

      await complianceReportsCustomizationService?.createScheduledDelivery({
        scheduleName: formData?.scheduleName?.trim(),
        reportTemplateId: formData?.reportTemplateId,
        deliveryFrequency: formData?.deliveryFrequency,
        deliveryDayOfWeek: formData?.deliveryFrequency === 'weekly' ? formData?.deliveryDayOfWeek : null,
        deliveryDayOfMonth: formData?.deliveryFrequency === 'monthly' ? formData?.deliveryDayOfMonth : null,
        deliveryTime: formData?.deliveryTime + ':00',
        reportFormat: formData?.reportFormat,
        recipientEmails: emails,
        recipientDepartments: formData?.recipientDepartments,
        nextDeliveryDate,
        isActive: true,
        createdBy: 'current_user@example.com' // Replace with actual user email
      });

      onSave?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Failed to create schedule');
    } finally {
      setSaving(false);
    }
  };

  const calculateNextDeliveryDate = (frequency, dayOfWeek, dayOfMonth) => {
    const now = new Date();
    const nextDate = new Date(now);

    if (frequency === 'daily') {
      nextDate?.setDate(now?.getDate() + 1);
    } else if (frequency === 'weekly') {
      const currentDay = now?.getDay() || 7;
      const daysUntilTarget = (dayOfWeek - currentDay + 7) % 7 || 7;
      nextDate?.setDate(now?.getDate() + daysUntilTarget);
    } else if (frequency === 'monthly') {
      nextDate?.setMonth(now?.getMonth() + 1);
      nextDate?.setDate(dayOfMonth);
    } else if (frequency === 'quarterly') {
      nextDate?.setMonth(now?.getMonth() + 3);
      nextDate?.setDate(1);
    }

    return nextDate?.toISOString();
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full p-6">
          <div className="flex items-center justify-center py-8">
            <Icon name="Loader" size={32} color="var(--color-muted-foreground)" className="animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
              <Icon name="Clock" size={24} />
              Schedule Automated Report
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Configure automated compliance report delivery in PNG Kina format
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-md flex items-start gap-2">
              <Icon name="AlertCircle" size={18} className="mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Schedule Name <span className="text-error">*</span>
            </label>
            <Input
              value={formData?.scheduleName}
              onChange={(e) => handleChange('scheduleName', e?.target?.value)}
              placeholder="e.g., Weekly Executive Report"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Report Template <span className="text-error">*</span>
            </label>
            <Select
              value={formData?.reportTemplateId}
              onChange={(e) => handleChange('reportTemplateId', e?.target?.value)}
              options={templates?.map(t => ({ value: t?.id, label: t?.templateName }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Frequency <span className="text-error">*</span>
              </label>
              <Select
                value={formData?.deliveryFrequency}
                onChange={(e) => handleChange('deliveryFrequency', e?.target?.value)}
                options={frequencyOptions}
              />
            </div>

            {formData?.deliveryFrequency === 'weekly' && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Day of Week
                </label>
                <Select
                  value={formData?.deliveryDayOfWeek}
                  onChange={(e) => handleChange('deliveryDayOfWeek', parseInt(e?.target?.value))}
                  options={dayOfWeekOptions}
                />
              </div>
            )}

            {formData?.deliveryFrequency === 'monthly' && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Day of Month
                </label>
                <Input
                  type="number"
                  min="1"
                  max="31"
                  value={formData?.deliveryDayOfMonth}
                  onChange={(e) => handleChange('deliveryDayOfMonth', parseInt(e?.target?.value))}
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Delivery Time
              </label>
              <Input
                type="time"
                value={formData?.deliveryTime}
                onChange={(e) => handleChange('deliveryTime', e?.target?.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Report Format <span className="text-error">*</span>
              </label>
              <Select
                value={formData?.reportFormat}
                onChange={(e) => handleChange('reportFormat', e?.target?.value)}
                options={formatOptions}
              />
            </div>
          </div>

          {formData?.reportFormat === 'png' && (
            <div className="bg-accent/10 border border-accent/20 rounded-md p-3">
              <div className="flex items-start gap-2">
                <Icon name="Info" size={16} color="var(--color-accent)" className="mt-0.5" />
                <div className="text-xs text-accent">
                  <p className="font-medium mb-1">PNG Format with Kina Currency</p>
                  <p>Reports will be generated as high-quality images with all costs displayed in PNG Kina (K) format, ideal for executive presentations.</p>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Recipient Emails
            </label>
            <Input
              value={formData?.recipientEmails}
              onChange={(e) => handleChange('recipientEmails', e?.target?.value)}
              placeholder="email1@company.com, email2@company.com"
            />
            <p className="text-xs text-muted-foreground mt-1">Separate multiple emails with commas</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Recipient Departments
            </label>
            <div className="grid grid-cols-2 gap-3">
              {departmentOptions?.map((dept) => (
                <Checkbox
                  key={dept}
                  checked={formData?.recipientDepartments?.includes(dept)}
                  onChange={() => handleDepartmentToggle(dept)}
                  label={dept}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted/20">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            iconName="Save"
            onClick={handleSave}
            loading={saving}
          >
            Create Schedule
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReportSchedulerModal;